import React, { useState, useEffect, useMemo } from 'react';
import { Box, TextField, Alert, Button, Typography, InputAdornment, Divider, Paper } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import InboxIcon from '@mui/icons-material/Inbox';
import { BulkTransactionItem, BulkApprovalModalData } from '../types/bulkTransaction.types';
import { formatMoney, currencyToForm } from '../utils/approvalUtils';
import { decrypt } from '../utils/decrypt';

interface BulkApprovalModalProps {
  data: BulkApprovalModalData;
  onApprove?: () => void;
  onCancel?: () => void;
}

const BulkApprovalModal: React.FC<BulkApprovalModalProps> = ({ data, onApprove, onCancel }) => {
  const [searchText, setSearchText] = useState('');
  const [filteredData, setFilteredData] = useState<BulkTransactionItem[]>([]);

  useEffect(() => {
    setFilteredData(data.transactionData || []);
  }, [data.transactionData]);

  // Filter transactions based on search text
  const handleSearch = (value: string) => {
    setSearchText(value);

    if (!value || value.trim() === '') {
      setFilteredData(data.transactionData || []);
      return;
    }

    const searchLower = value.toLowerCase();
    const filtered = (data.transactionData || []).filter((item: BulkTransactionItem) => {
      // Search in wallet number
      if (item.ReceiverWalletNo?.toLowerCase().includes(searchLower)) {
        return true;
      }
      // Search in phone number
      if (item.ReceiverPhoneNumber?.toLowerCase().includes(searchLower)) {
        return true;
      }
      // Search in card number (decrypt first before searching)
      if (item.ReceiverCardNo) {
        try {
          const decryptedCardNo = decrypt(item.ReceiverCardNo);
          if (decryptedCardNo.toLowerCase().includes(searchLower)) {
            return true;
          }
        } catch (error) {
          // If decryption fails, try searching in original value
          if (item.ReceiverCardNo.toLowerCase().includes(searchLower)) {
            return true;
          }
        }
      }
      // Search in description
      if (item.Description?.toLowerCase().includes(searchLower)) {
        return true;
      }
      // Search in amount
      if (item.Value?.toString().includes(searchLower)) {
        return true;
      }
      return false;
    });

    setFilteredData(filtered);
  };

  // Get receiver info based on transaction type
  const getReceiverInfo = (item: BulkTransactionItem): string => {
    if (item.ReceiverWalletNo) {
      return `Hesap: ${item.ReceiverWalletNo}`;
    }
    if (item.ReceiverPhoneNumber) {
      return `Telefon: ${item.ReceiverPhoneNumber}`;
    }
    if (item.ReceiverCardNo) {
      return `Kart No: ${decrypt(item.ReceiverCardNo)}`;
    }
    return 'Bilinmiyor';
  };

  // Calculate total amount
  const getTotalAmount = useMemo(() => {
    return filteredData.reduce((sum, item) => sum + (item.Value || 0), 0);
  }, [filteredData]);

  // Calculate total fee
  const getTotalFee = useMemo(() => {
    return filteredData.reduce((sum, item) => sum + (item.ValueFee || 0), 0);
  }, [filteredData]);

  return (
    <Box sx={{ p: 1.5 }}>
      {/* Message Area */}
      {data.message && (
        <Box sx={{ width: '100%', py: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Alert
            severity={data.messageType || 'info'}
            sx={{
              width: '100%',
              mb: 2,
              borderRadius: 1,
              '& .MuiAlert-message': {
                fontSize: '15px',
              },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {data.message}
            </Typography>
            {data.messageDescription && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                {data.messageDescription}
              </Typography>
            )}
          </Alert>
        </Box>
      )}

      {/* Search Filter */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Hesap No, Telefon, Açıklama veya Tutar ile arayın..."
          value={searchText}
          onChange={(e) => handleSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 1,
              minHeight: '50px',
            },
            '& .MuiInputBase-input::placeholder': {
              color: '#A0A0A0',
              fontSize: '15px',
            },
          }}
        />
      </Box>

      {/* Summary Information */}
      <Paper
        elevation={0}
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 2,
          mb: 2,
          p: 2,
          bgcolor: 'background.default',
          borderRadius: 1,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
            Toplam İşlem
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {filteredData.length} / {data.transactionData?.length || 0}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
            Toplam Tutar
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
            {formatMoney(getTotalAmount)}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
            Toplam Komisyon
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'warning.main' }}>
            {formatMoney(getTotalFee)}
          </Typography>
        </Box>
      </Paper>

      {/* Transaction List */}
      <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
        {filteredData.length > 0 ? (
          filteredData.map((item, index) => (
            <Paper
              key={`transaction-${item.OrderId || item.TransactionId || index}`}
              elevation={0}
              sx={{
                mb: 1.5,
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '&:hover': {
                  boxShadow: 2,
                },
                transition: 'box-shadow 0.3s',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        fontSize: '12px',
                        fontWeight: 600,
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {getReceiverInfo(item)}
                    </Typography>
                  </Box>
                  {item.Description && (
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'text.secondary',
                        ml: 4,
                        display: 'block',
                      }}
                    >
                      {item.Description}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>
                    {formatMoney(item.Value)}
                  </Typography>
                  {item.ValueFee > 0 && (
                    <Typography variant="body1" sx={{ color: 'text.secondary'  }}>
                      Komisyon: {formatMoney(item.ValueFee)}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Currency */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 4 }}>
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  Para Birimi:{' '}
                  <Typography component="span" variant="caption" sx={{ fontWeight: 500, color: 'text.primary' }}>
                    {currencyToForm(item.CurrencyId)}
                  </Typography>
                </Typography>
              </Box>
            </Paper>
          ))
        ) : (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              color: 'text.secondary',
            }}
          >
            <InboxIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
            <Typography variant="body2">Arama kriterine uygun işlem bulunamadı</Typography>
          </Box>
        )}
      </Box>

      {/* Modal Footer Buttons */}
      {data.showActionButtons !== false && (
        <>
          <Divider sx={{ mt: 3, mb: 2 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={onCancel}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '14px',
                px: 2.5,
                height: 40,
                borderRadius: 1,
              }}
            >
              İptal
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={onApprove}
              endIcon={<CheckIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '14px',
                px: 2.5,
                height: 40,
                borderRadius: 1,
              }}
            >
              Onayla
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default BulkApprovalModal;
